/**
 * Plyr Autoplay Module
 * Handles autoplay functionality for Plyr players when entering viewport
 */

import Plyr from "plyr";
import "plyr/dist/plyr.css";

class PlyrAutoplay {
  constructor() {
    this.players = new Map();
    this.observer = null;
    this.isInitialized = false;
  }

  /**
   * Initialize the module
   */
  init() {
    if (this.isInitialized) {
      return;
    }

    this.setupPlayers();
    this.setupIntersectionObserver();
    this.setupVisibilityChangeHandler();
    this.isInitialized = true;
  }

  /**
   * Get video sources from data attributes
   */
  getSourcesFromDataset(element) {
    const sources = [];
    if (element.dataset.srcHls) {
      sources.push({
        src: element.dataset.srcHls,
        type: "application/x-mpegURL",
      });
    }
    if (element.dataset.srcMp4) {
      sources.push({ src: element.dataset.srcMp4, type: "video/mp4" });
    }
    if (element.dataset.srcWebm) {
      sources.push({ src: element.dataset.srcWebm, type: "video/webm" });
    }
    return sources;
  }

  /**
   * Get player configuration from data attributes
   */
  getPlayerConfig(element) {
    return {
      // Control colors
      controlColor: element.dataset.controlColor || "#00b4d8", // Default blue

      // Autoplay settings
      autoplay:
        element.dataset.autoplay === "true" || element.dataset.autoplay === "1",

      // Loop settings
      loop: element.dataset.loop === "true" || element.dataset.loop === "1",

      // Controls visibility
      hideControls:
        element.dataset.hideControls === "true" ||
        element.dataset.hideControls === "1",

      // Muted (for autoplay)
      muted: element.dataset.muted !== "false" && element.dataset.muted !== "0", // Default true

      // Click to play
      clickToPlay:
        element.dataset.clickToPlay !== "false" &&
        element.dataset.clickToPlay !== "0", // Default true

      // Reset on end
      resetOnEnd:
        element.dataset.resetOnEnd === "true" ||
        element.dataset.resetOnEnd === "1",

      // Inactivity timeout
      inactivityTimeout: parseInt(element.dataset.inactivityTimeout) || 2000,

      // Tooltips
      tooltips: {
        controls:
          element.dataset.tooltips !== "false" &&
          element.dataset.tooltips !== "0", // Default true
        seek:
          element.dataset.seekTooltip !== "false" &&
          element.dataset.seekTooltip !== "0", // Default true
      },
    };
  }

  /**
   * Generate custom CSS for control colors
   */
  getControlCSS(controlColor) {
    return `
      .plyr--video .plyr__control--overlaid,
      .plyr--video .plyr__poster {
        display: none !important;
      }
      .plyr--video .plyr__control--overlaid {
        opacity: 0 !important;
        visibility: hidden !important;
      }
      /* Fix icon display - prevent jumping */
      .plyr__control {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .plyr__control svg {
        width: 18px;
        height: 18px;
        fill: currentColor;
        display: block;
        flex-shrink: 0;
      }
      .plyr__control--overlaid svg {
        width: 64px;
        height: 64px;
      }
      /* Prevent duplicate icons */
      .plyr__control button {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
      }
      .plyr__control button svg {
        width: 18px;
        height: 18px;
        fill: currentColor;
      }
      /* Hide any duplicate icons */
      .plyr__control button svg + svg {
        display: none;
      }
      /* Custom control colors */
      .plyr--video .plyr__control {
        color: ${controlColor} !important;
      }
      .plyr--video .plyr__control:hover {
        color: ${this.lightenColor(controlColor, 20)} !important;
      }
      .plyr--video .plyr__control[aria-pressed="true"] {
        color: ${this.darkenColor(controlColor, 20)} !important;
      }
      .plyr--video .plyr__progress__played {
        background: ${controlColor} !important;
      }
      .plyr--video .plyr__volume__progress {
        background: ${controlColor} !important;
      }
      .plyr--video .plyr__menu__container {
        border-color: ${controlColor} !important;
      }
      .plyr--video .plyr__menu__container .plyr__control[role="menuitemradio"][aria-checked="true"] {
        background: ${controlColor} !important;
      }
    `;
  }

  /**
   * Lighten a color by percentage
   */
  lightenColor(color, percent) {
    const num = parseInt(color.replace("#", ""), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = ((num >> 8) & 0x00ff) + amt;
    const B = (num & 0x0000ff) + amt;
    return (
      "#" +
      (
        0x1000000 +
        (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
        (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
        (B < 255 ? (B < 1 ? 0 : B) : 255)
      )
        .toString(16)
        .slice(1)
    );
  }

  /**
   * Darken a color by percentage
   */
  darkenColor(color, percent) {
    const num = parseInt(color.replace("#", ""), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) - amt;
    const G = ((num >> 8) & 0x00ff) - amt;
    const B = (num & 0x0000ff) - amt;
    return (
      "#" +
      (
        0x1000000 +
        (R > 255 ? 255 : R < 0 ? 0 : R) * 0x10000 +
        (G > 255 ? 255 : G < 0 ? 0 : G) * 0x100 +
        (B > 255 ? 255 : B < 0 ? 0 : B)
      )
        .toString(16)
        .slice(1)
    );
  }

  /**
   * Setup Plyr players for all video elements
   */
  setupPlayers() {
    // Debug: check all video elements
    const allVideos = document.querySelectorAll("video");

    // Look for video elements that need Plyr
    const videoElements = document.querySelectorAll("video");
    if (!videoElements.length) {
      return;
    }

    videoElements.forEach((el) => {
      this.setupPlayer(el);
    });
  }

  /**
   * Setup individual Plyr player
   */
  setupPlayer(el) {
    try {
      // Skip if already has Plyr instance
      if (el.plyr) {
        return;
      }

      // Get sources from data attributes
      const sources = this.getSourcesFromDataset(el);
      if (sources.length > 0) {
        // Set sources on video element
        el.src = sources[0].src;
        if (sources[0].type) {
          el.type = sources[0].type;
        }
      }

      // Get configuration from data attributes
      const config = this.getPlayerConfig(el);

      // Create Plyr instance with custom config
      const player = new Plyr(el, {
        controls: [
          "play",
          "progress",
          "current-time",
          "duration",
          "mute",
          "volume",
          "settings",
          "fullscreen",
        ],
        settings: ["quality", "speed"],
        quality: {
          default: 720,
          options: [1080, 720, 480, 360],
        },
        speed: {
          selected: 1,
          options: [0.5, 0.75, 1, 1.25, 1.5, 2],
        },
        keyboard: {
          focused: true,
          global: false,
        },
        tooltips: {
          controls: config.tooltips.controls,
          seek: config.tooltips.seek,
        },
        autoplay: config.autoplay,
        muted: config.muted,
        clickToPlay: config.clickToPlay,
        hideControls: config.hideControls,
        resetOnEnd: config.resetOnEnd,
        loop: {
          active: config.loop,
        },
        disableContextMenu: false,
        loadSprite: true,
        iconUrl: "https://cdn.plyr.io/3.7.8/plyr.svg",
        blankVideo: "https://cdn.plyr.io/static/blank.mp4",
        // Custom CSS for control colors
        css: this.getControlCSS(config.controlColor),
      });

      // Setup player when ready
      player.on("ready", () => {
        this.onPlayerReady(player, el, config);
      });

      // Set poster if available
      if (el.dataset.poster) {
        el.poster = el.dataset.poster;
      }

      this.players.set(el, player);
    } catch (error) {
      console.error("Error processing Plyr player:", error);
    }
  }

  /**
   * Handle player ready event
   */
  onPlayerReady(player, el, config) {
    // Setup event listeners
    this.setupPlayerEvents(player, el);

    // Ensure player is ready for interaction
    player.volume = 0; // Muted by default

    // Apply custom styling
    this.applyCustomStyling(el, config.controlColor);
  }

  /**
   * Apply custom styling to player
   */
  applyCustomStyling(el, controlColor) {
    // Add custom CSS for this specific player
    const style = document.createElement("style");
    style.id = `plyr-custom-${el.id}`;
    style.textContent = this.getControlCSS(controlColor);
    document.head.appendChild(style);

    // Also hide overlay via JavaScript as backup
    setTimeout(() => {
      const overlay = el.parentElement?.querySelector(
        ".plyr__control--overlaid"
      );
      if (overlay) {
        overlay.style.display = "none";
      }
    }, 100);
  }

  /**
   * Setup player event listeners
   */
  setupPlayerEvents(player, el) {
    player.on("seeking", () => {});

    player.on("seeked", () => {});

    player.on("timeupdate", () => {
      // Time update handling if needed
    });

    player.on("play", () => {
      // Play event handling if needed
    });

    player.on("pause", () => {
      // Pause event handling if needed
    });

    player.on("loadedmetadata", () => {
      // Metadata loaded handling if needed
    });

    player.on("canplay", () => {
      // Can play handling if needed
    });

    // Handle loop functionality
    player.on("ended", () => {
      const shouldLoop = el.dataset.loop === "true" || el.dataset.loop === "1";
      if (shouldLoop) {
        player.restart();
      }
    });
  }

  /**
   * Setup IntersectionObserver for autoplay on viewport entry
   */
  setupIntersectionObserver() {
    if (
      typeof window !== "undefined" &&
      typeof window.IntersectionObserver !== "undefined"
    ) {
      this.observer = new window.IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.handleIntersection(entry);
          });
        },
        { threshold: [0, 0.6, 1] }
      );

      this.players.forEach((_player, el) => {
        this.observer.observe(el);
      });
    } else {
      // Fallback for browsers without IntersectionObserver
      this.setupScrollFallback();
    }
  }

  /**
   * Handle intersection observer entry
   */
  handleIntersection(entry) {
    const el = entry.target;
    const player = this.players.get(el);
    if (!player) return;

    if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
      player.muted = true;

      // Always try to autoplay when entering view
      player.play().catch((error) => {
        console.warn("Video autoplay failed:", error);
      });
    } else {
      // Always pause when leaving view
      player.pause();
    }
  }

  /**
   * Setup scroll fallback for browsers without IntersectionObserver
   */
  setupScrollFallback() {
    this.players.forEach((player, el) => {
      const rect = el.getBoundingClientRect();
      const inView = rect.top < window.innerHeight && rect.bottom > 0;
      if (inView) {
        player.muted = true;
        player.play().catch(() => {});
      }

      let ticking = false;
      window.addEventListener(
        "scroll",
        () => {
          if (ticking) return;
          ticking = true;
          window.requestAnimationFrame(() => {
            const r = el.getBoundingClientRect();
            const visible =
              r.top < window.innerHeight * 0.4 &&
              r.bottom > window.innerHeight * 0.4;
            if (visible) {
              player.muted = true;
              player.play().catch((error) => {
                console.warn("Video autoplay failed (fallback):", error);
              });
            } else {
              player.pause();
            }
            ticking = false;
          });
        },
        { passive: true }
      );
    });
  }

  /**
   * Setup visibility change handler to pause videos when tab is hidden
   */
  setupVisibilityChangeHandler() {
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) {
        this.players.forEach((player) => player.pause());
      }
    });
  }

  /**
   * Observe new videos (for dynamic content)
   */
  observeNewVideos(container = document) {
    if (!this.isInitialized) return;

    const newVideos = container.querySelectorAll("video");
    newVideos.forEach((el) => {
      // Skip if already processed
      if (!this.players.has(el)) {
        this.setupPlayer(el);
        if (this.observer) {
          this.observer.observe(el);
        }
      }
    });
  }

  /**
   * Destroy the module and clean up resources
   */
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    this.players.forEach((player) => {
      try {
        player.destroy();
      } catch (error) {
        console.error("Error destroying player:", error);
      }
    });

    this.players.clear();
    this.isInitialized = false;
  }
}

export default PlyrAutoplay;
